<template>
  <v-container fluid class="pa-6">
    <v-card elevation="2">
      <v-card-title class="d-flex align-center justify-space-between pa-4">
        <div>
          <h2 class="text-h5 font-weight-bold">Teams Directory</h2>
          <p class="text-subtitle-2 text--secondary mt-1 mb-0" v-if="lastUpdate">
            Last updated: {{ formatDate(lastUpdate) }}
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          label="Search teams..."
          single-line
          hide-details
          outlined
          dense
          style="max-width: 400px"
          @input="debouncedSearch"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="teams"
          :loading="loading"
          :server-items-length="totalItems"
          :options.sync="options"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100]
          }"
          class="elevation-0"
        >
          <template v-slot:item.site_logo="{ item }">
            <v-avatar size="40" class="my-2">
              <v-img
                :src="item.site_logo"
                :alt="item.client_name"
                @error="onImageError"
              >
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-icon>mdi-account-group</v-icon>
                  </v-row>
                </template>
              </v-img>
            </v-avatar>
          </template>

          <template v-slot:item.client_name="{ item }">
            <div class="font-weight-medium">{{ item.client_name }}</div>
          </template>

          <template v-slot:item.project_type="{ item }">
            <v-chip small outlined color="primary">
              {{ item.project_type }}
            </v-chip>
          </template>

          <template v-slot:item.country="{ item }">
            <div class="d-flex align-center">
              <v-icon small class="mr-1">mdi-map-marker</v-icon>
              {{ item.country }}
            </div>
          </template>

          <template v-slot:item.colors="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom v-if="item.colors && item.colors.primary">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    :style="{
                      backgroundColor: item.colors.primary,
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      marginRight: '4px',
                      border: '1px solid #e0e0e0'
                    }"
                  ></div>
                </template>
                <span>Primary: {{ item.colors.primary }}</span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.colors && item.colors.secondary">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    :style="{
                      backgroundColor: item.colors.secondary,
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }"
                  ></div>
                </template>
                <span>Secondary: {{ item.colors.secondary }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.routing_value="{ item }">
            <a 
              v-if="item.routing_value" 
              :href="item.routing_value" 
              target="_blank" 
              class="text-decoration-none"
            >
              <v-chip small color="info" outlined>
                <v-icon small left>mdi-open-in-new</v-icon>
                Visit
              </v-chip>
            </a>
          </template>

          <template v-slot:item.version="{ item }">
            <v-chip x-small outlined>
              v{{ item.version }}
            </v-chip>
          </template>

          <template v-slot:loading>
            <v-skeleton-loader
              v-for="i in 10"
              :key="i"
              type="table-row"
            ></v-skeleton-loader>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-8">
              <v-icon size="64" color="grey lighten-1">mdi-account-group-outline</v-icon>
              <p class="text-h6 mt-4">No teams found</p>
              <p class="text-body-2 text--secondary">
                {{ searchQuery ? 'Try adjusting your search criteria' : 'No teams data available' }}
              </p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import TeamService from '@/services/TeamService'

export default {
  name: 'Teams',
  data: () => ({
    teams: [],
    loading: false,
    searchQuery: '',
    totalItems: 0,
    lastUpdate: null,
    searchTimeout: null,
    options: {
      page: 1,
      itemsPerPage: 20,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: false
    },
    headers: [
      { text: 'Logo', value: 'site_logo', sortable: false, width: '80px' },
      { text: 'Team Name', value: 'client_name', sortable: false },
      { text: 'Project Type', value: 'project_type', sortable: false },
      { text: 'Country', value: 'country', sortable: false },
      { text: 'Colors', value: 'colors', sortable: false, width: '100px' },
      { text: 'Link', value: 'routing_value', sortable: false, width: '100px' },
      { text: 'Version', value: 'version', sortable: false, width: '100px' },
    ]
  }),
  watch: {
    options: {
      handler() {
        this.fetchTeams()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchTeams()
  },
  methods: {
    async fetchTeams() {
      this.loading = true
      try {
        const { page, itemsPerPage } = this.options
        const response = await TeamService.getTeamsJson({
          search: this.searchQuery,
          page: page,
          pageSize: itemsPerPage
        })
        
        // Response structure: response.data.data = { success, data, total, lastUpdate, ... }
        if (response.data.data && response.data.data.success) {
          this.teams = response.data.data.data || []
          this.totalItems = response.data.data.total || 0
          this.lastUpdate = response.data.data.lastUpdate || null
        } else {
          this.$vs.notification({
            title: 'Error',
            text: response.data.data?.message || response.data.message || 'Failed to fetch teams',
            color: 'danger',
            position: 'top-right'
          })
        }
      } catch (error) {
        console.error('Error fetching teams:', error)
        this.$vs.notification({
          title: 'Error',
          text: 'Failed to fetch teams data',
          color: 'danger',
          position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1
        this.fetchTeams()
      }, 500)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    onImageError(event) {
      event.target.style.display = 'none'
    }
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  min-height: 400px;
}
</style>

